import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { auth0Config } from "../Auth/auth0Config";

const AUTH0_DB_CONNECTION = "Username-Password-Authentication";

function getChangePasswordUrl(email?: string | null): string {
	const domain = auth0Config.domain?.replace(/^https?:\/\//, "").replace(/\/$/, "") ?? "";
	const clientId = auth0Config.clientId ?? "";
	const base = domain ? `https://${domain}` : "";
	let url = `${base}/dbconnections/change_password?client_id=${encodeURIComponent(clientId)}&connection=${encodeURIComponent(AUTH0_DB_CONNECTION)}`;
	if (email) {
		url += `&email=${encodeURIComponent(email)}`;
	}
	return url;
}

/** Account settings card with password change and Auth0 re-authentication. */
export function AccountSettingsCard(): React.ReactElement {
	const { user, loginWithRedirect } = useAuth0();
	const changePasswordUrl = getChangePasswordUrl(user?.email);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
			<h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Account</h2>
			<p className="mt-1 text-xs text-slate-500">
				Change password and re-authenticate via Auth0.
			</p>

			{user && (
				<div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3">
					<p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Signed in as</p>
					<p className="mt-1 truncate text-sm font-medium text-slate-800">
						{user.name ?? user.nickname ?? user.email ?? "—"}
					</p>
					{user.email && user.email !== (user.name ?? user.nickname) && (
						<p className="mt-0.5 truncate text-xs text-slate-500">{user.email}</p>
					)}
				</div>
			)}

			<div className="mt-6 space-y-3">
				<a
					href={changePasswordUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					<span>Change password</span>
					<span className="text-slate-400">→</span>
				</a>
				<button
					type="button"
					onClick={() =>
						loginWithRedirect({
							authorizationParams: { prompt: "login" },
						})
					}
					className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					<span>Re-authenticate</span>
					<span className="text-slate-400">→</span>
				</button>
			</div>

			<p className="mt-4 text-[11px] text-slate-500">
				Change password opens Auth0’s hosted page in a new tab (database users only). Re-authenticate signs you in again (e.g. to refresh session).
			</p>
		</div>
	);
}
